import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { ChatMessage } from "../widget/state";
import { renderMarkdown } from "../render/markdown";

@customElement("cg-message-bubble")
export class CGMessageBubble extends LitElement {
  static override styles = css`
    :host {
      display: block;
      max-width: 100%;
    }

    .bubble {
      max-width: 82%;
      padding: 10px 14px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 1.45;
      word-wrap: break-word;
      white-space: normal;
      animation: fadeInUp 120ms ease-out;
    }

    /* User bubble */
    :host([variant="USER"]) .bubble {
      background: var(--cg-bubble-user-bg, var(--cg-chat-bubble));
      color: var(--cg-bubble-user-text, var(--cg-on-primary));
      border-radius: 20px;
      font-weight: 500;
    }

    /* agent bubble */
    :host([variant="AGENT"]) .bubble {
      background: var(--cg-bubble-agent-bg, var(--cg-surface));
      color: var(--cg-bubble-agent-text, var(--cg-text));
      border: 1px solid var(--cg-border);
      border-radius: 12px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      font-weight: 500;
    }

    /* Streaming pulse */
    :host([streaming]) .bubble {
      opacity: 0.9;
    }

    /* ===========================
   Markdown Typography
=========================== */

    .bubble > * {
      line-height: 1.5;
      white-space: normal;
    }

    /* Headings */

    .bubble h1,
    .bubble h2,
    .bubble h3,
    .bubble h4 {
      font-weight: 600;
      line-height: 1.25;
      margin: 0.6em 0 0.4em;
    }

    .bubble h1 {
      font-size: 1.15em;
    }
    .bubble h2 {
      font-size: 1.08em;
    }
    .bubble h3 {
      font-size: 1.02em;
    }
    .bubble h4 {
      font-size: 1em;
    }

    /* Paragraphs */

    .bubble p {
      margin: 0.45em 0;
    }

    .bubble p:first-child {
      margin-top: 0;
    }

    .bubble p:last-child {
      margin-bottom: 0;
    }

    /* Lists */

    .bubble ul,
    .bubble ol {
      padding-left: 1.1em;
      margin: 0.4em 0;
    }

    .bubble li {
      margin: 0.2em 0;
    }

    .bubble li::marker {
      color: var(--cg-muted);
    }

    /* Blockquotes */

    .bubble blockquote {
      margin: 0.5em 0;
      padding: 0.4em 0.6em;
      border-left: 3px solid var(--cg-border);
      background: color-mix(in srgb, var(--cg-bg) 92%, transparent);
      border-radius: 6px;
      color: var(--cg-muted);
      font-style: italic;
    }

    /* Inline code */

    .bubble code {
      font-family: var(--cg-font);
      background: color-mix(in srgb, var(--cg-bg) 88%, black);
      padding: 2px 5px;
      border-radius: 6px;
      font-size: 12px;
    }

    /* Code blocks */

    .bubble pre {
      background: color-mix(in srgb, var(--cg-bg) 90%, black);
      padding: 10px 12px;
      border-radius: 10px;
      overflow-x: auto;
      font-size: 12px;
      line-height: 1.4;
      margin: 0.5em 0;
    }

    .bubble pre code {
      background: transparent;
      padding: 0;
      font-size: inherit;
    }

    /* Tables */

    .bubble table {
      width: 100%;
      border-collapse: collapse;
      margin: 0.5em 0;
      font-size: 12.5px;
    }

    .bubble th,
    .bubble td {
      border: 1px solid var(--cg-border);
      padding: 6px 8px;
      text-align: left;
    }

    .bubble th {
      background: color-mix(in srgb, var(--cg-bg) 90%, black);
      font-weight: 600;
    }

    /* Links */

    .bubble a {
      color: var(--cg-bubble-agent-text);
      text-decoration: none;
      font-weight: 500;
    }

    .bubble a:hover {
      text-decoration: underline;
    }

    /* Horizontal rule */

    .bubble hr {
      border: none;
      border-top: 1px solid var(--cg-border);
      margin: 0.6em 0;
    }

    /* Images */

    .bubble img {
      max-width: 100%;
      border-radius: 8px;
      margin: 0.4em 0;
    }

    @keyframes fadeInUp {
      from {
        transform: translateY(2px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  `;

  @property({ attribute: false }) message?: ChatMessage;
  @property({ type: String, reflect: true }) variant:
    | "USER"
    | "AGENT"
    | "SYSTEM" = "AGENT";

  override render() {
    if (!this.message) return nothing;

    const content = this.message.content ?? "";

    // User messages render as text (Lit safely escapes)
    if (this.variant === "USER") {
      return html`<div class="bubble">${content}</div>`;
    }

    // agent messages render sanitized markdown HTML
    const safe = renderMarkdown(content);
    return html`<div class="bubble">${unsafeHTML(safe)}</div>`;
  }
}
